export type UserRole = "visitor" | "exhibitor" | "organizer" | "moderator";
export type ExhibitorRole = "company_owner" | "representative" | "booth_staff";

/** Статус выставки в БД (ТЗ) */
export type EventDbStatus = "draft" | "active" | "finished" | "archived";

/** Статус для UI-клиентов */
export type EventUiStatus = "upcoming" | "active" | "past";

/**
 * Статус по календарным датам проведения.
 * - past: дата окончания уже прошла (сегодня ещё считается актуальным)
 * - active: сегодня входит в [startDate … endDate]
 * - upcoming: начало ещё впереди
 */
export function deriveEventStatusFromDates(
  startDate: string | Date,
  endDate: string | Date,
  now: Date = new Date(),
): EventUiStatus {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "upcoming";
  }

  const dayKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const today = dayKey(now);
  const startDay = dayKey(start);
  const endDay = dayKey(end);

  if (endDay < today) return "past";
  if (startDay > today) return "upcoming";
  return "active";
}

/** Ближайшая дата старта сверху (ASC). */
export function compareEventsByNearestStart(
  a: { startDate: string; endDate?: string },
  b: { startDate: string; endDate?: string },
): number {
  const as = new Date(a.startDate).getTime();
  const bs = new Date(b.startDate).getTime();
  if (as !== bs) return as - bs;
  return new Date(a.endDate ?? a.startDate).getTime() - new Date(b.endDate ?? b.startDate).getTime();
}

/**
 * Список для UI: актуальные (active/upcoming) сверху по ближайшей дате,
 * прошедшие — внизу (недавние сверху среди прошлых).
 */
export function compareEventsForListing(
  a: { status: EventUiStatus; startDate: string; endDate?: string },
  b: { status: EventUiStatus; startDate: string; endDate?: string },
): number {
  const rank = (s: EventUiStatus) => (s === "past" ? 1 : 0);
  const ra = rank(a.status);
  const rb = rank(b.status);
  if (ra !== rb) return ra - rb;
  if (a.status === "past" && b.status === "past") {
    return new Date(b.endDate ?? b.startDate).getTime() - new Date(a.endDate ?? a.startDate).getTime();
  }
  return compareEventsByNearestStart(a, b);
}

export function mapDbStatusToUi(status: EventDbStatus): EventUiStatus {
  switch (status) {
    case "draft":
      return "upcoming";
    case "active":
      return "active";
    case "finished":
    case "archived":
      return "past";
  }
}

export function mapUiStatusToDb(status: EventUiStatus): EventDbStatus {
  switch (status) {
    case "upcoming":
      return "draft";
    case "active":
      return "active";
    case "past":
      return "finished";
  }
}
